// pages/Reports.js — GROLEE Analytics Dashboard
// Architecture: load all collections ONCE on mount, reprocess client-side on filter change

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box, Grid, Typography, Tab, Tabs, Button, FormControl, InputLabel,
  Select, MenuItem, Alert, Snackbar, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Refresh as RefreshCw,
  AttachMoney as DollarSign,
  ShoppingCart,
  People as Users,
  Inventory as Package,
  BarChart as BarChart3,
  VerifiedUser,
  LocalOffer,
  Stars,
  FileDownload,
} from '@mui/icons-material';
import AdminLayout from '../components/AdminLayout';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

// ─── Chart.js registration ────────────────────────────────────────────────────
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend,
);

// ─── Theme-consistent chart palette ──────────────────────────────────────────
const CHART_COLORS = [
  '#10b981', '#06b6d4', '#8b5cf6', '#f59e0b',
  '#ef4444', '#34d399', '#38bdf8', '#a78bfa',
];

// ─── Shared chart base options ────────────────────────────────────────────────
const BASE_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: { color: '#c8d8c8', font: { family: 'Inter', size: 12 } },
    },
    tooltip: {
      backgroundColor: '#252b25',
      titleColor: '#f0f9f0',
      bodyColor: '#c8d8c8',
      borderColor: '#374151',
      borderWidth: 1,
    },
  },
  scales: {
    x: { ticks: { color: '#9db59d' }, grid: { color: '#2d3748' } },
    y: { ticks: { color: '#9db59d' }, grid: { color: '#2d3748' }, beginAtZero: true },
  },
};

// ─── Line chart area-gradient plugin (defined once, stable reference) ─────────
const areaGradientPlugin = {
  id: 'areaGradient',
  beforeDraw(chart) {
    const { ctx, chartArea, data } = chart;
    if (!chartArea || !data.datasets[0]) return;
    const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    g.addColorStop(0, 'rgba(16,185,129,0.35)');
    g.addColorStop(1, 'rgba(16,185,129,0.0)');
    data.datasets[0].backgroundColor = g;
  },
};

// ─── Doughnut center-label plugins ────────────────────────────────────────────
const centerRevenueLabelPlugin = {
  id: 'centerRevenue',
  afterDatasetsDraw(chart) {
    const { ctx, width, height } = chart;
    const total = chart.data.datasets[0]?.data.reduce((a, b) => a + b, 0) || 0;
    ctx.save();
    ctx.font = 'bold 11px Inter';
    ctx.fillStyle = '#f0f9f0';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      'RM ' + total.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      width / 2,
      height / 2,
    );
    ctx.restore();
  },
};

const centerCountLabelPlugin = {
  id: 'centerCount',
  afterDatasetsDraw(chart) {
    const { ctx, width, height } = chart;
    const total = chart.data.datasets[0]?.data.reduce((a, b) => a + b, 0) || 0;
    ctx.save();
    ctx.font = 'bold 16px Inter';
    ctx.fillStyle = '#f0f9f0';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(total.toLocaleString('en-MY'), width / 2, height / 2);
    ctx.restore();
  },
};

// ─── Pure utility functions ───────────────────────────────────────────────────

function toDate(raw) {
  if (!raw) return null;
  if (raw.toDate) return raw.toDate();
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function getDateBounds(period) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (period) {
    case 'today':
      return { startDate: today, endDate: new Date(today.getTime() + 86400000 - 1) };
    case 'yesterday': {
      const y = new Date(today.getTime() - 86400000);
      return { startDate: y, endDate: new Date(y.getTime() + 86400000 - 1) };
    }
    case 'thisWeek': {
      const ws = new Date(today);
      ws.setDate(today.getDate() - today.getDay());
      return { startDate: ws, endDate: now };
    }
    case 'thisMonth':
      return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
    case 'lastMonth': {
      const ls = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const le = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      return { startDate: ls, endDate: le };
    }
    case 'last30Days': {
      return { startDate: new Date(today.getTime() - 30 * 86400000), endDate: now };
    }
    default:
      return null; // allTime
  }
}

function getPurchaseAmount(p) {
  return parseFloat(p.amount_paid ?? p.total_amount ?? 0) || 0;
}

function getPurchaseDate(p) {
  return toDate(p.purchase_date ?? p.timestamp ?? p.created_at);
}

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-MY', {
    style: 'currency', currency: 'MYR', minimumFractionDigits: 2,
  }).format(amount || 0);

const formatNumber = (num) => new Intl.NumberFormat('en-MY').format(num || 0);

const formatPercentage = (num, dec = 1) => `${(num || 0).toFixed(dec)}%`;

function buildMonthlyBuckets(monthsBack) {
  const now = new Date();
  const labels = [], keys = [], map = {};
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    labels.push(d.toLocaleDateString('en-MY', { month: 'short', year: '2-digit' }));
    keys.push(key);
    map[key] = 0;
  }
  return { labels, keys, map };
}

// ─── Styled Components ────────────────────────────────────────────────────────

const ReportsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const ReportsHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-lg);
  flex-wrap: wrap;
  gap: var(--space-md);
`;

const PageTitle = styled('h1')`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);
  svg { width: 40px; height: 40px; color: var(--accent-primary); }
`;

const FilterContainer = styled('div')`
  display: flex;
  gap: var(--space-md);
  align-items: center;
  flex-wrap: wrap;
`;

const MetricGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-lg);
  margin-bottom: var(--space-xl);
`;

const KpiCard = styled('div')`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  position: relative;
  overflow: hidden;
  transition: all var(--transition-normal);
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${p => p.accentColor || 'var(--accent-gradient)'};
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 35px var(--shadow);
    border-color: var(--accent-primary);
  }
`;

const KpiLabel = styled('p')`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin: 0 0 var(--space-sm);
`;

const KpiValue = styled('h3')`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 var(--space-xs);
`;

const KpiSubtitle = styled('p')`
  font-size: 0.8rem;
  color: var(--text-tertiary);
  margin: 0;
`;

const KpiIconBox = styled('div')`
  width: 48px; height: 48px;
  border-radius: var(--radius-md);
  background: ${p => p.bgColor || 'rgba(16,185,129,0.1)'};
  display: flex; align-items: center; justify-content: center;
  color: ${p => p.iconColor || 'var(--accent-primary)'};
  flex-shrink: 0;
  svg { width: 24px; height: 24px; }
`;

const ChartSection = styled('div')`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
`;

const SectionHeading = styled('h3')`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 var(--space-md);
  display: flex; align-items: center; gap: var(--space-sm);
  svg { width: 20px; height: 20px; color: var(--accent-primary); }
`;

const InsightsPanel = styled('div')`
  background: var(--surface);
  border: 1px solid var(--border-subtle, #2d3748);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
`;

const EmptyState = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-md);
  padding: var(--space-2xl, 48px);
  color: var(--text-tertiary);
  font-size: 0.9rem;
  text-align: center;
  svg { width: 40px; height: 40px; opacity: 0.4; }
`;

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid var(--border);
  margin-bottom: var(--space-xl);
  .MuiTab-root {
    color: var(--text-secondary);
    font-weight: 600;
    text-transform: none;
    &.Mui-selected { color: var(--accent-primary); }
  }
  .MuiTabs-indicator { background-color: var(--accent-primary); }
`;

const StyledTableContainer = styled(TableContainer)`
  background: transparent;
  border-radius: var(--radius-md);
  .MuiTableCell-head {
    color: var(--text-primary);
    font-weight: 700;
    background: #252b25;
    border-color: var(--border);
  }
  .MuiTableCell-body {
    color: var(--text-secondary);
    border-color: var(--border-subtle, #2d3748);
  }
  .MuiTableRow-root:hover { background: var(--surface-hover, #3a4a3a); }
`;

// ─── KPI Metric Card component ────────────────────────────────────────────────
function KpiMetricCard({ title, value, subtitle, icon: Icon, iconColor, bgColor, accentColor, isLoading }) {
  return (
    <KpiCard accentColor={accentColor}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box flex={1} mr={1}>
          <KpiLabel>{title}</KpiLabel>
          {isLoading
            ? <CircularProgress size={22} sx={{ color: 'var(--accent-primary)', mt: 0.5 }} />
            : <KpiValue>{value}</KpiValue>
          }
          {subtitle && <KpiSubtitle>{subtitle}</KpiSubtitle>}
        </Box>
        {Icon && (
          <KpiIconBox bgColor={bgColor} iconColor={iconColor}>
            <Icon />
          </KpiIconBox>
        )}
      </Box>
    </KpiCard>
  );
}

// ─── Status Badge component ───────────────────────────────────────────────────
function StatusBadge({ label, color = '#10b981' }) {
  return (
    <Box component="span" sx={{
      display: 'inline-block',
      px: 1.5, py: 0.5,
      borderRadius: 1,
      fontSize: '0.75rem',
      fontWeight: 700,
      backgroundColor: color + '22',
      color,
    }}>
      {label}
    </Box>
  );
}

// ─── Main Reports component ───────────────────────────────────────────────────
function Reports() {

  // ── State ─────────────────────────────────────────────────────────────────
  const [rawAll, setRawAll]               = useState(null);
  const [dateRange, setDateRange]         = useState('allTime');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter]   = useState('all');
  const [currentTab, setCurrentTab]       = useState(0);
  const [isLoading, setIsLoading]         = useState(true);
  const [isExporting, setIsExporting]     = useState(false);
  const [error, setError]                 = useState(null);
  const [snackbar, setSnackbar]           = useState({ open: false, message: '', severity: 'success' });

  // ── Chart refs (for PDF capture) ──────────────────────────────────────────
  const revenueTrendRef = useRef(null);
  const categoryRef     = useRef(null);
  const monthlyBarRef   = useRef(null);
  const topProductsRef  = useRef(null);
  const userGrowthRef   = useRef(null);
  const membershipRef   = useRef(null);
  const cartFunnelRef   = useRef(null);
  const trolleyRef      = useRef(null);

  // ── Data loading — fetches all 6 collections ONCE ─────────────────────────
  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [pSnap, prSnap, catSnap, uSnap, caSnap, tSnap] = await Promise.all([
        getDocs(collection(db, 'purchases')),
        getDocs(collection(db, 'products')),
        getDocs(collection(db, 'categories')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'carts')),
        getDocs(collection(db, 'trolleys')),
      ]);
      setRawAll({
        purchases:  pSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        products:   prSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        categories: catSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        users:      uSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        carts:      caSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        trolleys:   tSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      });
    } catch (err) {
      console.error('Reports loadAllData:', err);
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  // ── filteredPurchases — client-side filter only, no Firestore calls ────────
  const filteredPurchases = useMemo(() => {
    if (!rawAll) return [];
    let list = rawAll.purchases;
    const bounds = getDateBounds(dateRange);
    if (bounds) {
      list = list.filter(p => {
        const d = getPurchaseDate(p);
        return d && d >= bounds.startDate && d <= bounds.endDate;
      });
    }
    if (statusFilter !== 'all') {
      list = list.filter(p => p.status === statusFilter);
    }
    return list;
  }, [rawAll, dateRange, statusFilter]);

  // ── overviewMetrics ───────────────────────────────────────────────────────
  const overviewMetrics = useMemo(() => {
    if (!rawAll) return {};
    let totalRevenue = 0, discountImpact = 0, pointsDiscountTotal = 0;
    let completedCount = 0, cancelledCount = 0;

    filteredPurchases.forEach(p => {
      const paid     = getPurchaseAmount(p);
      const original = parseFloat(p.total_amount ?? 0) || 0;
      totalRevenue         += paid;
      discountImpact       += Math.max(0, original - paid);
      pointsDiscountTotal  += parseFloat(p.points_discount ?? 0) || 0;
      if (p.status === 'completed') completedCount++;
      if (p.status === 'cancelled') cancelledCount++;
    });

    const totalTransactions = filteredPurchases.length;
    const avgOrderValue     = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

    const sevenDaysAgo = new Date(Date.now() - 7 * 86400000);
    const activeUsers7d = rawAll.users.filter(u => {
      const d = toDate(u.last_login_date);
      return d && d >= sevenDaysAgo;
    }).length;

    const memberCount       = rawAll.users.filter(u => u.is_member === true).length;
    const totalLoyaltyPoints = rawAll.users.reduce((s, u) => s + (parseInt(u.points) || 0), 0);
    const totalCarts        = rawAll.carts.length;
    const verifiedCarts     = rawAll.carts.filter(c => c.guard_verified === true).length;
    const completedCarts    = rawAll.carts.filter(c => c.status === 'completed').length;

    return {
      totalRevenue, avgOrderValue, totalTransactions,
      discountImpact, pointsDiscountTotal,
      completedCount, cancelledCount,
      activeUsers7d, memberCount,
      totalUsers:      rawAll.users.length,
      totalLoyaltyPoints,
      totalCarts, verifiedCarts, completedCarts,
      guardVerificationRate: totalCarts > 0 ? (verifiedCarts / totalCarts) * 100 : 0,
      cartCompletionRate:    totalCarts > 0 ? (completedCarts / totalCarts) * 100 : 0,
    };
  }, [filteredPurchases, rawAll]);

  // ── topProducts ───────────────────────────────────────────────────────────
  const topProducts = useMemo(() => {
    if (!rawAll) return [];
    const catMap = {};
    rawAll.categories.forEach(c => { catMap[c.id] = c.name || c.id; });
    const prodCatMap = {};
    rawAll.products.forEach(p => { prodCatMap[p.id] = p.category_id; });

    const productMap = {};
    filteredPurchases.forEach(p => {
      if (!Array.isArray(p.items)) return;
      p.items.forEach(item => {
        const pid = item.product_id;
        if (!pid) return;
        const catId = prodCatMap[pid];
        if (categoryFilter !== 'all' && catId !== categoryFilter) return;
        if (!productMap[pid]) {
          productMap[pid] = {
            id: pid,
            name: item.name || 'Unknown',
            quantity: 0,
            revenue: 0,
            unitPrice: item.price || 0,
            categoryName: catMap[catId] || 'Uncategorized',
          };
        }
        productMap[pid].quantity += item.quantity || 1;
        productMap[pid].revenue  += (item.price || 0) * (item.quantity || 1);
      });
    });
    return Object.values(productMap).sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  }, [filteredPurchases, rawAll, categoryFilter]);

  // ── categoryBreakdown ─────────────────────────────────────────────────────
  const categoryBreakdown = useMemo(() => {
    if (!rawAll) return [];
    const catRevMap = {};
    rawAll.categories.forEach(c => {
      catRevMap[c.id] = { id: c.id, name: c.name || c.id, revenue: 0, quantity: 0 };
    });
    const prodCatMap = {};
    rawAll.products.forEach(p => { prodCatMap[p.id] = p.category_id; });

    filteredPurchases.forEach(p => {
      if (!Array.isArray(p.items)) return;
      p.items.forEach(item => {
        const catId = prodCatMap[item.product_id];
        if (catId && catRevMap[catId]) {
          catRevMap[catId].revenue  += (item.price || 0) * (item.quantity || 1);
          catRevMap[catId].quantity += item.quantity || 1;
        }
      });
    });

    const sorted = Object.values(catRevMap)
      .filter(c => c.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue);
    const totalRev = sorted.reduce((s, c) => s + c.revenue, 0);
    return sorted.map(c => ({ ...c, pct: totalRev > 0 ? (c.revenue / totalRev) * 100 : 0 }));
  }, [filteredPurchases, rawAll]);

  // ── revenueTrendData — respects date window ───────────────────────────────
  const revenueTrendData = useMemo(() => {
    if (!rawAll) return null;
    const purchases = filteredPurchases;
    const bounds    = getDateBounds(dateRange);

    if (dateRange === 'today' || dateRange === 'yesterday') {
      const buckets = {};
      const labels  = [];
      for (let h = 0; h < 24; h++) {
        const k = `${String(h).padStart(2, '0')}:00`;
        labels.push(k);
        buckets[k] = 0;
      }
      purchases.forEach(p => {
        const d = getPurchaseDate(p);
        if (!d) return;
        const k = `${String(d.getHours()).padStart(2, '0')}:00`;
        if (Object.prototype.hasOwnProperty.call(buckets, k)) buckets[k] += getPurchaseAmount(p);
      });
      return {
        labels,
        datasets: [{ label: 'Revenue (RM)', data: labels.map(k => buckets[k]), borderColor: '#10b981', tension: 0.4, fill: true, pointRadius: 3, pointBackgroundColor: '#10b981' }],
      };
    }

    if (dateRange === 'allTime') {
      const { labels, keys, map } = buildMonthlyBuckets(24);
      purchases.forEach(p => {
        const d = getPurchaseDate(p);
        if (!d) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (Object.prototype.hasOwnProperty.call(map, key)) map[key] += getPurchaseAmount(p);
      });
      return {
        labels,
        datasets: [{ label: 'Revenue (RM)', data: keys.map(k => map[k]), borderColor: '#10b981', tension: 0.4, fill: true, pointRadius: 2, pointBackgroundColor: '#10b981' }],
      };
    }

    // Daily buckets for all other ranges
    const startDate = bounds?.startDate || new Date(Date.now() - 30 * 86400000);
    const endDate   = bounds?.endDate   || new Date();
    const days      = [];
    const dayMap    = {};
    const cur       = new Date(startDate);
    while (cur <= endDate) {
      const key   = cur.toISOString().split('T')[0];
      const label = cur.toLocaleDateString('en-MY', { month: 'short', day: 'numeric' });
      days.push({ key, label });
      dayMap[key] = 0;
      cur.setDate(cur.getDate() + 1);
    }
    purchases.forEach(p => {
      const d = getPurchaseDate(p);
      if (!d) return;
      const key = d.toISOString().split('T')[0];
      if (Object.prototype.hasOwnProperty.call(dayMap, key)) dayMap[key] += getPurchaseAmount(p);
    });
    return {
      labels: days.map(d => d.label),
      datasets: [{ label: 'Revenue (RM)', data: days.map(d => dayMap[d.key]), borderColor: '#10b981', tension: 0.4, fill: true, pointRadius: 3, pointBackgroundColor: '#10b981' }],
    };
  }, [filteredPurchases, rawAll, dateRange]);

  // ── monthlyRevenueData — last 12 months bar ───────────────────────────────
  const monthlyRevenueData = useMemo(() => {
    if (!rawAll) return null;
    const { labels, keys, map } = buildMonthlyBuckets(12);
    filteredPurchases.forEach(p => {
      const d = getPurchaseDate(p);
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (Object.prototype.hasOwnProperty.call(map, key)) map[key] += getPurchaseAmount(p);
    });
    return {
      labels,
      datasets: [{ label: 'Revenue (RM)', data: keys.map(k => map[k]), backgroundColor: '#10b981', borderRadius: 4, hoverBackgroundColor: '#34d399' }],
    };
  }, [filteredPurchases, rawAll]);

  // ── userGrowthData — monthly new users, last 12 months ───────────────────
  const userGrowthData = useMemo(() => {
    if (!rawAll) return null;
    const { labels, keys, map } = buildMonthlyBuckets(12);
    rawAll.users.forEach(u => {
      const d = toDate(u.joined_date ?? u.created_at);
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (Object.prototype.hasOwnProperty.call(map, key)) map[key]++;
    });
    return {
      labels,
      datasets: [{ label: 'New Users', data: keys.map(k => map[k]), backgroundColor: '#8b5cf6', borderRadius: 4, hoverBackgroundColor: '#a78bfa' }],
    };
  }, [rawAll]);

  // ── membershipChartData ───────────────────────────────────────────────────
  const membershipChartData = useMemo(() => {
    if (!rawAll) return null;
    const memberCount  = rawAll.users.filter(u => u.is_member === true).length;
    const regularCount = rawAll.users.length - memberCount;
    return {
      labels: ['Members', 'Regular'],
      datasets: [{ data: [memberCount, regularCount], backgroundColor: ['#10b981', '#06b6d4'], borderColor: '#2d342d', borderWidth: 3 }],
    };
  }, [rawAll]);

  // ── cartFunnelData ────────────────────────────────────────────────────────
  const cartFunnelData = useMemo(() => {
    if (!rawAll) return null;
    const statuses = ['completed', 'checked_out', 'active', 'abandoned'];
    const labels   = ['Completed', 'Checked Out', 'Active', 'Abandoned'];
    const colors   = ['#10b981', '#06b6d4', '#f59e0b', '#ef4444'];
    return {
      labels,
      datasets: [{
        label: 'Carts',
        data: statuses.map(s => rawAll.carts.filter(c => c.status === s).length),
        backgroundColor: colors,
        borderRadius: 4,
      }],
    };
  }, [rawAll]);

  // ── trolleyStatusData ─────────────────────────────────────────────────────
  const trolleyStatusData = useMemo(() => {
    if (!rawAll) return { total: 0, available: 0, paired: 0, maintenance: 0 };
    return {
      total:       rawAll.trolleys.length,
      available:   rawAll.trolleys.filter(t => t.status === 'available').length,
      paired:      rawAll.trolleys.filter(t => t.status === 'paired' || t.status === 'in_use').length,
      maintenance: rawAll.trolleys.filter(t => t.status === 'maintenance').length,
    };
  }, [rawAll]);

  const trolleyChartData = useMemo(() => {
    if (!rawAll) return null;
    return {
      labels: ['Available', 'In Use', 'Maintenance'],
      datasets: [{ data: [trolleyStatusData.available, trolleyStatusData.paired, trolleyStatusData.maintenance], backgroundColor: ['#10b981', '#06b6d4', '#f59e0b'], borderColor: '#2d342d', borderWidth: 3 }],
    };
  }, [rawAll, trolleyStatusData]);

  // ── dealPerformance ───────────────────────────────────────────────────────
  const dealPerformance = useMemo(() => {
    if (!rawAll) return [];
    const catMap = {};
    rawAll.categories.forEach(c => { catMap[c.id] = c.name || c.id; });
    const dealProducts = rawAll.products.filter(p => p.is_deal === true);
    const sales = {};
    filteredPurchases.forEach(p => {
      if (!Array.isArray(p.items)) return;
      p.items.forEach(item => {
        if (!sales[item.product_id]) sales[item.product_id] = { quantity: 0, revenue: 0 };
        sales[item.product_id].quantity += item.quantity || 1;
        sales[item.product_id].revenue  += (item.price || 0) * (item.quantity || 1);
      });
    });
    return dealProducts.map(p => ({
      id: p.id,
      name: p.name,
      price:         p.promotional_price || p.price || 0,
      originalPrice: p.previous_price    || p.price || 0,
      discount:      p.price_drop_percent || 0,
      category:      catMap[p.category_id] || 'Uncategorized',
      unitsSold:     sales[p.id]?.quantity || 0,
      revenue:       sales[p.id]?.revenue  || 0,
    })).sort((a, b) => b.revenue - a.revenue);
  }, [rawAll, filteredPurchases]);

  // ── paymentMethodData ─────────────────────────────────────────────────────
  const paymentMethodData = useMemo(() => {
    if (!rawAll) return null;
    const map = {};
    filteredPurchases.forEach(p => {
      const m = p.payment_method || 'Unknown';
      map[m] = (map[m] || 0) + 1;
    });
    const labels = Object.keys(map);
    if (labels.length === 0) return null;
    return {
      labels,
      datasets: [{ data: Object.values(map), backgroundColor: CHART_COLORS, borderColor: '#2d342d', borderWidth: 3 }],
    };
  }, [filteredPurchases, rawAll]);

  // ── smartInsights ─────────────────────────────────────────────────────────
  const smartInsights = useMemo(() => {
    if (!rawAll || overviewMetrics.totalTransactions === 0) return [];
    const insights = [];
    if (categoryBreakdown.length > 0) {
      insights.push(`Top category "${categoryBreakdown[0].name}" drives ${formatPercentage(categoryBreakdown[0].pct)} of revenue.`);
    }
    if (overviewMetrics.pointsDiscountTotal > 0) {
      insights.push(`Loyalty points saved customers ${formatCurrency(overviewMetrics.pointsDiscountTotal)} this period.`);
    }
    const cancRate = overviewMetrics.totalTransactions > 0
      ? (overviewMetrics.cancelledCount / overviewMetrics.totalTransactions) * 100
      : 0;
    if (cancRate > 15) {
      insights.push(`${formatPercentage(cancRate)} of purchases were cancelled — consider reviewing the checkout experience.`);
    }
    if (overviewMetrics.guardVerificationRate > 0) {
      insights.push(`Guard verification completed for ${formatPercentage(overviewMetrics.guardVerificationRate)} of cart sessions.`);
    }
    if (topProducts.length > 0) {
      insights.push(`Best-selling product: "${topProducts[0].name}" with ${formatCurrency(topProducts[0].revenue)} in revenue.`);
    }
    return insights;
  }, [overviewMetrics, categoryBreakdown, topProducts, rawAll]);

  // ── PDF export ────────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async () => {
    setIsExporting(true);
    try {
      const { jsPDF }              = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc       = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const dateLabel = dateRange === 'allTime' ? 'All Time' : dateRange;

      // Page 1 — cover
      doc.setFillColor(10, 15, 10);
      doc.rect(0, 0, 210, 297, 'F');
      doc.setFillColor(16, 185, 129);
      doc.rect(0, 0, 210, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(36); doc.setTextColor(240, 249, 240);
      doc.text('GROLEE', 20, 70);
      doc.setFontSize(18); doc.setTextColor(200, 216, 200);
      doc.text('Analytics Report', 20, 84);
      doc.setFontSize(12);
      doc.text(`Period: ${dateLabel}`, 20, 102);
      doc.text(`Generated: ${new Date().toLocaleString('en-MY')}`, 20, 112);
      doc.setFillColor(16, 185, 129);
      doc.rect(20, 124, 60, 1.5, 'F');

      // Page 2 — KPIs
      doc.addPage();
      doc.setFillColor(10, 15, 10); doc.rect(0, 0, 210, 297, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(240, 249, 240);
      doc.text('Key Performance Indicators', 14, 20);
      autoTable(doc, {
        startY: 28,
        head: [['Metric', 'Value']],
        body: [
          ['Total Revenue',           formatCurrency(overviewMetrics.totalRevenue)],
          ['Total Transactions',      formatNumber(overviewMetrics.totalTransactions)],
          ['Average Order Value',     formatCurrency(overviewMetrics.avgOrderValue)],
          ['Discount Impact',         formatCurrency(overviewMetrics.discountImpact)],
          ['Points Discounts Given',  formatCurrency(overviewMetrics.pointsDiscountTotal)],
          ['Active Users (7 days)',   formatNumber(overviewMetrics.activeUsers7d)],
          ['Guard Verification Rate', formatPercentage(overviewMetrics.guardVerificationRate)],
          ['Cart Completion Rate',    formatPercentage(overviewMetrics.cartCompletionRate)],
        ],
        headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [45, 52, 45] },
        styles: { textColor: [240, 249, 240], fillColor: [37, 43, 37] },
        theme: 'grid',
      });

      // Revenue trend chart image
      if (revenueTrendRef.current) {
        const img = revenueTrendRef.current.toBase64Image();
        doc.addPage();
        doc.setFillColor(10, 15, 10); doc.rect(0, 0, 210, 297, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(240, 249, 240);
        doc.text('Revenue Trend', 14, 20);
        doc.addImage(img, 'PNG', 14, 28, 182, 95);
      }

      // Top Products table
      if (topProducts.length > 0) {
        doc.addPage();
        doc.setFillColor(10, 15, 10); doc.rect(0, 0, 210, 297, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(240, 249, 240);
        doc.text('Top Products by Revenue', 14, 20);
        autoTable(doc, {
          startY: 28,
          head: [['Rank', 'Product', 'Category', 'Units Sold', 'Revenue (RM)']],
          body: topProducts.map((p, i) => [i + 1, p.name, p.categoryName, formatNumber(p.quantity), formatCurrency(p.revenue)]),
          headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [45, 52, 45] },
          styles: { textColor: [240, 249, 240], fillColor: [37, 43, 37] },
          theme: 'grid',
        });
      }

      // Category breakdown table
      if (categoryBreakdown.length > 0) {
        doc.addPage();
        doc.setFillColor(10, 15, 10); doc.rect(0, 0, 210, 297, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(240, 249, 240);
        doc.text('Revenue by Category', 14, 20);
        autoTable(doc, {
          startY: 28,
          head: [['Category', 'Revenue (RM)', 'Items Sold', '% of Total']],
          body: categoryBreakdown.map(c => [c.name, formatCurrency(c.revenue), formatNumber(c.quantity), formatPercentage(c.pct)]),
          headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [45, 52, 45] },
          styles: { textColor: [240, 249, 240], fillColor: [37, 43, 37] },
          theme: 'grid',
        });
      }

      doc.save(`grolee-report-${dateLabel.replace(/\s+/g, '-').toLowerCase()}.pdf`);
      setSnackbar({ open: true, message: 'PDF exported successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Export failed: ${err.message}`, severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [overviewMetrics, topProducts, categoryBreakdown, dateRange]);

  // ── Chart option objects ───────────────────────────────────────────────────
  const lineOpts = useMemo(() => ({
    ...BASE_OPTS,
    scales: {
      ...BASE_OPTS.scales,
      y: { ...BASE_OPTS.scales.y, ticks: { color: '#9db59d', callback: v => 'RM ' + v.toLocaleString('en-MY') } },
    },
    plugins: { ...BASE_OPTS.plugins, legend: { display: false } },
  }), []);

  const barRevenueOpts = useMemo(() => ({
    ...BASE_OPTS,
    scales: {
      ...BASE_OPTS.scales,
      y: { ...BASE_OPTS.scales.y, ticks: { color: '#9db59d', callback: v => 'RM ' + v.toLocaleString('en-MY') } },
    },
    plugins: { ...BASE_OPTS.plugins, legend: { display: false } },
  }), []);

  const hBarRevenueOpts = useMemo(() => ({
    ...BASE_OPTS,
    indexAxis: 'y',
    scales: {
      x: { ticks: { color: '#9db59d', callback: v => 'RM ' + v.toLocaleString('en-MY') }, grid: { color: '#2d3748' } },
      y: { ticks: { color: '#9db59d' }, grid: { display: false } },
    },
    plugins: { ...BASE_OPTS.plugins, legend: { display: false } },
  }), []);

  const hBarCountOpts = useMemo(() => ({
    ...BASE_OPTS,
    indexAxis: 'y',
    scales: {
      x: { ticks: { color: '#9db59d' }, grid: { color: '#2d3748' } },
      y: { ticks: { color: '#9db59d' }, grid: { display: false } },
    },
    plugins: { ...BASE_OPTS.plugins, legend: { display: false } },
  }), []);

  const barCountOpts = useMemo(() => ({
    ...BASE_OPTS,
    plugins: { ...BASE_OPTS.plugins, legend: { display: false } },
  }), []);

  const doughnutOpts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { position: 'bottom', labels: { color: '#c8d8c8', font: { family: 'Inter', size: 12 }, padding: 16 } },
      tooltip: BASE_OPTS.plugins.tooltip,
    },
  }), []);

  // ── Full-page initial loading ─────────────────────────────────────────────
  if (isLoading && !rawAll) {
    return (
      <AdminLayout>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
          <CircularProgress sx={{ color: 'var(--accent-primary)' }} size={48} />
          <Typography sx={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>
            Loading analytics data…
          </Typography>
        </Box>
      </AdminLayout>
    );
  }

  const m = overviewMetrics;

  // ── Tab 0: Overview ────────────────────────────────────────────────────────
  const renderOverview = () => (
    <Box>
      <MetricGrid>
        <KpiMetricCard title="Total Revenue"      value={formatCurrency(m.totalRevenue)}       subtitle={`${m.totalTransactions} transactions`}          icon={DollarSign}   iconColor="#10b981" bgColor="rgba(16,185,129,0.1)"  accentColor="linear-gradient(135deg,#10b981,#34d399)" isLoading={isLoading} />
        <KpiMetricCard title="Avg Order Value"    value={formatCurrency(m.avgOrderValue)}       subtitle="Per transaction"                                 icon={ShoppingCart}  iconColor="#06b6d4" bgColor="rgba(6,182,212,0.1)"   accentColor="linear-gradient(135deg,#06b6d4,#38bdf8)" isLoading={isLoading} />
        <KpiMetricCard title="Active Users (7d)"  value={formatNumber(m.activeUsers7d)}         subtitle={`of ${formatNumber(m.totalUsers)} total users`}   icon={Users}         iconColor="#8b5cf6" bgColor="rgba(139,92,246,0.1)"  accentColor="linear-gradient(135deg,#8b5cf6,#a78bfa)" isLoading={isLoading} />
        <KpiMetricCard title="Guard Verification" value={formatPercentage(m.guardVerificationRate)} subtitle={`${m.verifiedCarts} of ${m.totalCarts} carts`} icon={VerifiedUser} iconColor="#f59e0b" bgColor="rgba(245,158,11,0.1)"  accentColor="linear-gradient(135deg,#f59e0b,#fbbf24)" isLoading={isLoading} />
      </MetricGrid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <ChartSection>
            <SectionHeading><BarChart3 />Revenue Trend</SectionHeading>
            <Box sx={{ height: 300 }}>
              {revenueTrendData && revenueTrendData.datasets[0].data.some(v => v > 0) ? (
                <Line ref={revenueTrendRef} data={revenueTrendData} options={lineOpts} plugins={[areaGradientPlugin]} />
              ) : (
                <EmptyState><BarChart3 /><span>No revenue data for this period</span></EmptyState>
              )}
            </Box>
          </ChartSection>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartSection>
            <SectionHeading><LocalOffer />Sales by Category</SectionHeading>
            <Box sx={{ height: 300 }}>
              {categoryBreakdown.length > 0 ? (
                <Doughnut
                  ref={categoryRef}
                  data={{
                    labels: categoryBreakdown.map(c => c.name),
                    datasets: [{ data: categoryBreakdown.map(c => c.revenue), backgroundColor: CHART_COLORS, borderColor: '#2d342d', borderWidth: 3 }],
                  }}
                  options={doughnutOpts}
                  plugins={[centerRevenueLabelPlugin]}
                />
              ) : (
                <EmptyState><LocalOffer /><span>No category data available</span></EmptyState>
              )}
            </Box>
          </ChartSection>
        </Grid>
      </Grid>

      <InsightsPanel>
        <SectionHeading><Stars />Smart Insights</SectionHeading>
        {smartInsights.length > 0
          ? smartInsights.map((ins, i) => (
              <Alert
                key={i}
                severity="info"
                sx={{
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'rgba(6,182,212,0.12)',
                  border: '1px solid rgba(6,182,212,0.3)',
                  color: '#e2f8ff',
                  '& .MuiAlert-icon': { color: '#38bdf8' },
                  '& .MuiAlert-message': { color: '#e2f8ff' },
                  mb: 1,
                }}
              >
                {ins}
              </Alert>
            ))
          : (
              <Typography sx={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                Not enough transaction data to generate insights. Try selecting a broader date range or check that purchases exist.
              </Typography>
            )
        }
      </InsightsPanel>
    </Box>
  );

  // ── Tab 1: Revenue ─────────────────────────────────────────────────────────
  const renderRevenue = () => (
    <Box>
      <MetricGrid>
        <KpiMetricCard title="Total Revenue"    value={formatCurrency(m.totalRevenue)}          subtitle={`${m.completedCount} completed orders`}     icon={DollarSign}  iconColor="#10b981" bgColor="rgba(16,185,129,0.1)" accentColor="linear-gradient(135deg,#10b981,#34d399)" isLoading={isLoading} />
        <KpiMetricCard title="Avg Order Value"  value={formatCurrency(m.avgOrderValue)}          subtitle="Per transaction"                            icon={ShoppingCart} iconColor="#06b6d4" bgColor="rgba(6,182,212,0.1)"  accentColor="linear-gradient(135deg,#06b6d4,#38bdf8)" isLoading={isLoading} />
        <KpiMetricCard title="Discount Impact"  value={formatCurrency(m.discountImpact)}         subtitle="Total savings given to customers"           icon={LocalOffer}  iconColor="#8b5cf6" bgColor="rgba(139,92,246,0.1)" accentColor="linear-gradient(135deg,#8b5cf6,#a78bfa)" isLoading={isLoading} />
        <KpiMetricCard title="Points Redeemed"  value={formatCurrency(m.pointsDiscountTotal)}   subtitle="Loyalty discounts applied"                  icon={Stars}        iconColor="#f59e0b" bgColor="rgba(245,158,11,0.1)" accentColor="linear-gradient(135deg,#f59e0b,#fbbf24)" isLoading={isLoading} />
      </MetricGrid>

      <Box sx={{ mb: 3 }}>
        <ChartSection>
          <SectionHeading><BarChart3 />Monthly Revenue (Last 12 Months)</SectionHeading>
          <Box sx={{ height: 260 }}>
            {monthlyRevenueData && monthlyRevenueData.datasets[0].data.some(v => v > 0) ? (
              <Bar ref={monthlyBarRef} data={monthlyRevenueData} options={barRevenueOpts} />
            ) : (
              <EmptyState><BarChart3 /><span>No monthly revenue data available</span></EmptyState>
            )}
          </Box>
        </ChartSection>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <ChartSection>
            <SectionHeading><Package />Top 10 Products by Revenue</SectionHeading>
            <Box sx={{ height: 360 }}>
              {topProducts.length > 0 ? (
                <Bar
                  ref={topProductsRef}
                  data={{
                    labels: topProducts.map(p => p.name.length > 22 ? p.name.slice(0, 22) + '…' : p.name),
                    datasets: [{ label: 'Revenue (RM)', data: topProducts.map(p => p.revenue), backgroundColor: '#06b6d4', borderRadius: 4, hoverBackgroundColor: '#38bdf8' }],
                  }}
                  options={hBarRevenueOpts}
                />
              ) : (
                <EmptyState><Package /><span>No product sales data for this period</span></EmptyState>
              )}
            </Box>
          </ChartSection>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartSection>
            <SectionHeading><ShoppingCart />Payment Methods</SectionHeading>
            <Box sx={{ height: 360 }}>
              {paymentMethodData ? (
                <Doughnut data={paymentMethodData} options={doughnutOpts} plugins={[centerCountLabelPlugin]} />
              ) : (
                <EmptyState><ShoppingCart /><span>No payment method data available</span></EmptyState>
              )}
            </Box>
          </ChartSection>
        </Grid>
      </Grid>

      <ChartSection>
        <SectionHeading><BarChart3 />Revenue by Category</SectionHeading>
        {categoryBreakdown.length > 0 ? (
          <StyledTableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Items Sold</TableCell>
                  <TableCell align="right">% of Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryBreakdown.map((cat, i) => (
                  <TableRow key={cat.id}>
                    <TableCell>
                      <StatusBadge label={i + 1} color={i === 0 ? '#10b981' : i === 1 ? '#f59e0b' : '#06b6d4'} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{cat.name}</TableCell>
                    <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>{formatCurrency(cat.revenue)}</TableCell>
                    <TableCell align="right">{formatNumber(cat.quantity)}</TableCell>
                    <TableCell align="right">{formatPercentage(cat.pct)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        ) : (
          <EmptyState><BarChart3 /><span>No category data for this period</span></EmptyState>
        )}
      </ChartSection>
    </Box>
  );

  // ── Tab 2: Customers ───────────────────────────────────────────────────────
  const renderCustomers = () => {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000);
    const inactiveCount = rawAll ? rawAll.users.filter(u => {
      const d = toDate(u.last_login_date);
      return !d || d < thirtyDaysAgo;
    }).length : 0;

    const segmentRows = [
      { label: 'Premium Members',      count: m.memberCount,           color: '#f59e0b', badge: 'Member'   },
      { label: 'Regular Users',        count: m.totalUsers - m.memberCount, color: '#06b6d4', badge: 'Regular'  },
      { label: 'Active (last 7 days)', count: m.activeUsers7d,          color: '#10b981', badge: 'Active'   },
      { label: 'Inactive (>30 days)',  count: inactiveCount,            color: '#ef4444', badge: 'Inactive' },
    ];

    return (
      <Box>
        <MetricGrid>
          <KpiMetricCard title="Total Users"        value={formatNumber(m.totalUsers)}         subtitle="Registered accounts"                                        icon={Users}       iconColor="#10b981" bgColor="rgba(16,185,129,0.1)" accentColor="linear-gradient(135deg,#10b981,#34d399)" isLoading={isLoading} />
          <KpiMetricCard title="Premium Members"    value={formatNumber(m.memberCount)}         subtitle={`${formatPercentage((m.memberCount / Math.max(m.totalUsers, 1)) * 100)} of users`} icon={Stars}  iconColor="#f59e0b" bgColor="rgba(245,158,11,0.1)" accentColor="linear-gradient(135deg,#f59e0b,#fbbf24)" isLoading={isLoading} />
          <KpiMetricCard title="Active (7 days)"    value={formatNumber(m.activeUsers7d)}       subtitle="Recent logins"                                               icon={VerifiedUser} iconColor="#8b5cf6" bgColor="rgba(139,92,246,0.1)" accentColor="linear-gradient(135deg,#8b5cf6,#a78bfa)" isLoading={isLoading} />
          <KpiMetricCard title="Total Loyalty Pts." value={formatNumber(m.totalLoyaltyPoints)} subtitle="Across all accounts"                                         icon={Stars}        iconColor="#06b6d4" bgColor="rgba(6,182,212,0.1)"  accentColor="linear-gradient(135deg,#06b6d4,#38bdf8)" isLoading={isLoading} />
        </MetricGrid>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={7}>
            <ChartSection>
              <SectionHeading><BarChart3 />New User Registrations (Last 12 Months)</SectionHeading>
              <Box sx={{ height: 280 }}>
                {userGrowthData && userGrowthData.datasets[0].data.some(v => v > 0) ? (
                  <Bar ref={userGrowthRef} data={userGrowthData} options={barCountOpts} />
                ) : (
                  <EmptyState><Users /><span>No registration data available</span></EmptyState>
                )}
              </Box>
            </ChartSection>
          </Grid>
          <Grid item xs={12} lg={5}>
            <ChartSection>
              <SectionHeading><Stars />Membership Split</SectionHeading>
              <Box sx={{ height: 280 }}>
                {membershipChartData && m.totalUsers > 0 ? (
                  <Doughnut ref={membershipRef} data={membershipChartData} options={doughnutOpts} plugins={[centerCountLabelPlugin]} />
                ) : (
                  <EmptyState><Users /><span>No user data available</span></EmptyState>
                )}
              </Box>
            </ChartSection>
          </Grid>
        </Grid>

        <ChartSection>
          <SectionHeading><Users />Customer Segmentation</SectionHeading>
          <StyledTableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Segment</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">% of Total</TableCell>
                  <TableCell>Label</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {segmentRows.map(row => (
                  <TableRow key={row.label}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                    <TableCell align="right">{formatNumber(row.count)}</TableCell>
                    <TableCell align="right">{formatPercentage((row.count / Math.max(m.totalUsers, 1)) * 100)}</TableCell>
                    <TableCell><StatusBadge label={row.badge} color={row.color} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </ChartSection>
      </Box>
    );
  };

  // ── Tab 3: Operations ──────────────────────────────────────────────────────
  const renderOperations = () => (
    <Box>
      <MetricGrid>
        <KpiMetricCard title="Total Trolleys" value={formatNumber(trolleyStatusData.total)}         subtitle="Fleet capacity"                                        icon={Package}     iconColor="#10b981" bgColor="rgba(16,185,129,0.1)" accentColor="linear-gradient(135deg,#10b981,#34d399)" isLoading={isLoading} />
        <KpiMetricCard title="In Use"         value={formatNumber(trolleyStatusData.paired)}         subtitle={`of ${trolleyStatusData.total} total`}                  icon={ShoppingCart} iconColor="#06b6d4" bgColor="rgba(6,182,212,0.1)"  accentColor="linear-gradient(135deg,#06b6d4,#38bdf8)" isLoading={isLoading} />
        <KpiMetricCard title="Verified Carts" value={formatNumber(m.verifiedCarts)}                 subtitle={`${formatPercentage(m.guardVerificationRate)} rate`}     icon={VerifiedUser} iconColor="#8b5cf6" bgColor="rgba(139,92,246,0.1)" accentColor="linear-gradient(135deg,#8b5cf6,#a78bfa)" isLoading={isLoading} />
        <KpiMetricCard title="Active Deals"   value={formatNumber(dealPerformance.length)}           subtitle="Deal products in catalog"                              icon={LocalOffer}  iconColor="#f59e0b" bgColor="rgba(245,158,11,0.1)" accentColor="linear-gradient(135deg,#f59e0b,#fbbf24)" isLoading={isLoading} />
      </MetricGrid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={7}>
          <ChartSection>
            <SectionHeading><ShoppingCart />Cart Status Breakdown</SectionHeading>
            <Box sx={{ height: 280 }}>
              {cartFunnelData && rawAll && rawAll.carts.length > 0 ? (
                <Bar ref={cartFunnelRef} data={cartFunnelData} options={hBarCountOpts} />
              ) : (
                <EmptyState><ShoppingCart /><span>No cart data available</span></EmptyState>
              )}
            </Box>
          </ChartSection>
        </Grid>
        <Grid item xs={12} lg={5}>
          <ChartSection>
            <SectionHeading><Package />Trolley Fleet Status</SectionHeading>
            <Box sx={{ height: 280 }}>
              {trolleyChartData && trolleyStatusData.total > 0 ? (
                <Doughnut ref={trolleyRef} data={trolleyChartData} options={doughnutOpts} plugins={[centerCountLabelPlugin]} />
              ) : (
                <EmptyState><Package /><span>No trolley data available</span></EmptyState>
              )}
            </Box>
          </ChartSection>
        </Grid>
      </Grid>

      {dealPerformance.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ChartSection>
            <SectionHeading><LocalOffer />Deal Performance</SectionHeading>
            <StyledTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Deal Price</TableCell>
                    <TableCell align="right">Original</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Units Sold</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dealPerformance.map(p => (
                    <TableRow key={p.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                      <TableCell>{p.category}</TableCell>
                      <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>{formatCurrency(p.price)}</TableCell>
                      <TableCell align="right" sx={{ color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>{formatCurrency(p.originalPrice)}</TableCell>
                      <TableCell align="right"><StatusBadge label={`-${p.discount.toFixed(0)}%`} color="#f59e0b" /></TableCell>
                      <TableCell align="right">{formatNumber(p.unitsSold)}</TableCell>
                      <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>{formatCurrency(p.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </ChartSection>
        </Box>
      )}

      <ChartSection>
        <SectionHeading><Package />Trolley Fleet Detail</SectionHeading>
        <StyledTableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">% of Fleet</TableCell>
                <TableCell>Indicator</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { label: 'Available',   count: trolleyStatusData.available,   color: '#10b981' },
                { label: 'In Use',      count: trolleyStatusData.paired,       color: '#06b6d4' },
                { label: 'Maintenance', count: trolleyStatusData.maintenance,  color: '#f59e0b' },
              ].map(row => (
                <TableRow key={row.label}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                  <TableCell align="right">{formatNumber(row.count)}</TableCell>
                  <TableCell align="right">
                    {formatPercentage(trolleyStatusData.total > 0 ? (row.count / trolleyStatusData.total) * 100 : 0)}
                  </TableCell>
                  <TableCell><StatusBadge label={row.label} color={row.color} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </ChartSection>
    </Box>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <ReportsContainer>

        {/* Header */}
        <ReportsHeader>
          <PageTitle>
            <BarChart3 />
            Reports &amp; Analytics
          </PageTitle>

          <FilterContainer>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: 'var(--text-secondary)' }}>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={e => setDateRange(e.target.value)}
                sx={{ color: 'var(--text-primary)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' } }}
              >
                <MenuItem value="allTime">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="lastMonth">Last Month</MenuItem>
                <MenuItem value="last30Days">Last 30 Days</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }} disabled={currentTab !== 1}>
              <InputLabel sx={{ color: 'var(--text-secondary)' }}>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={e => setCategoryFilter(e.target.value)}
                sx={{ color: 'var(--text-primary)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' } }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {rawAll?.categories.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: 'var(--text-secondary)' }}>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={e => setStatusFilter(e.target.value)}
                sx={{ color: 'var(--text-primary)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' } }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={isLoading ? <CircularProgress size={14} /> : <RefreshCw />}
              onClick={loadAllData}
              disabled={isLoading}
              size="small"
              sx={{
                borderColor: 'var(--border)',
                color: 'var(--text-primary)',
                '&:hover': { borderColor: 'var(--accent-primary)', color: 'var(--accent-primary)' },
              }}
            >
              {isLoading ? 'Loading…' : 'Refresh'}
            </Button>

            <Button
              variant="contained"
              startIcon={isExporting
                ? <CircularProgress size={14} sx={{ color: 'white' }} />
                : <FileDownload />}
              onClick={handleExportPDF}
              disabled={isExporting || !rawAll}
              size="small"
              sx={{
                background: 'var(--accent-gradient)',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 20px rgba(16,185,129,0.4)' },
              }}
            >
              {isExporting ? 'Exporting…' : 'Export PDF'}
            </Button>
          </FilterContainer>
        </ReportsHeader>

        {/* Error banner */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 'var(--radius-md)' }}
            action={
              <Button size="small" onClick={loadAllData} sx={{ color: 'inherit' }}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <StyledTabs value={currentTab} onChange={(_, v) => setCurrentTab(v)}>
          <Tab label="Overview"   />
          <Tab label="Revenue"    />
          <Tab label="Customers"  />
          <Tab label="Operations" />
        </StyledTabs>

        {currentTab === 0 && renderOverview()}
        {currentTab === 1 && renderRevenue()}
        {currentTab === 2 && renderCustomers()}
        {currentTab === 3 && renderOperations()}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            severity={snackbar.severity}
            sx={{ borderRadius: 'var(--radius-md)' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ReportsContainer>
    </AdminLayout>
  );
}

export default Reports;
